/**
 * DOTPayItem - Represents a complete DOT (Department of Transportation) pay item
 * with all associated data including pay item details, quantities, costs, funding, etc.
 * This is an aggregate class that brings together all related domain objects.
 */
import { PayItem } from "./PayItem";
import { Quantity } from "./Quantity";
import { Funding } from "./Funding";
import { Cost } from "./Cost";
import { Time } from "./Time";
import { ConstructionPhase } from "./ConstructionPhase";
import { PlanPrep } from "./PlanPrep";
import { Tabulation } from "./Tabulation";
import { QAQC } from "./QAQC";
import { Asset } from "../assets/Asset";
import { Help } from "../common/Help";
import { Remarks } from "../common/Remarks";

export class DOTPayItem {
  /** Optional slot index (1..5) when sourced from DOT_PayItem{n} */
  Index?: number;
  /** Specification book filter */
  SPECBOOK_FILTER: string;

  /** Reference item search term */
  REFITEM_SEARCH: string;

  /** Pay item information */
  PayItem: PayItem;

  /** Quantity calculation information */
  Quantity: Quantity;

  /** Funding source information */
  Funding: Funding;

  /** Cost information */
  Cost: Cost;

  /** Time estimation information */
  Time: Time;

  /** Construction phase information */
  ConstructionPhase: ConstructionPhase;

  /** Plan preparation information */
  PlanPrep: PlanPrep;

  /** Tabulation information */
  Tabulation: Tabulation;

  /** Asset information */
  Asset: Asset;

  /** Quality Assurance/Quality Control information */
  QAQC: QAQC;

  /** Help information */
  Help: Help;

  /** Remarks information */
  Remarks: Remarks;

  /**
   * Creates a new DOTPayItem instance
   * @param data - Object containing DOTPayItem properties
   */
  constructor(data: {
    Index?: number;
    SPECBOOK_FILTER: string;
    REFITEM_SEARCH: string;
    PayItem: PayItem;
    Quantity: Quantity;
    Funding: Funding;
    Cost: Cost;
    Time: Time;
    ConstructionPhase: ConstructionPhase;
    PlanPrep: PlanPrep;
    Tabulation: Tabulation;
    Asset: Asset;
    QAQC: QAQC;
    Help: Help;
    Remarks: Remarks;
  }) {
    this.Index = data.Index;
    this.SPECBOOK_FILTER = data.SPECBOOK_FILTER;
    this.REFITEM_SEARCH = data.REFITEM_SEARCH;
    this.PayItem = data.PayItem;
    this.Quantity = data.Quantity;
    this.Funding = data.Funding;
    this.Cost = data.Cost;
    this.Time = data.Time;
    this.ConstructionPhase = data.ConstructionPhase;
    this.PlanPrep = data.PlanPrep;
    this.Tabulation = data.Tabulation;
    this.Asset = data.Asset;
    this.QAQC = data.QAQC;
    this.Help = data.Help;
    this.Remarks = data.Remarks;
  }

  /**
   * Creates a DOTPayItem from a plain object (useful for deserialization)
   * @param obj - Plain object with DOTPayItem data
   * @returns New DOTPayItem instance
   */
  static fromObject(obj: Partial<DOTPayItem>): DOTPayItem {
    return new DOTPayItem({
      Index: obj.Index,
      SPECBOOK_FILTER: obj.SPECBOOK_FILTER ?? "",
      REFITEM_SEARCH: obj.REFITEM_SEARCH ?? "",
      PayItem: obj.PayItem
        ? PayItem.fromObject(obj.PayItem)
        : PayItem.fromObject({}),
      Quantity: obj.Quantity
        ? Quantity.fromObject(obj.Quantity)
        : Quantity.fromObject({}),
      Funding: obj.Funding
        ? Funding.fromObject(obj.Funding)
        : Funding.fromObject({}),
      Cost: obj.Cost ? Cost.fromObject(obj.Cost) : Cost.fromObject({}),
      Time: obj.Time ? Time.fromObject(obj.Time) : Time.fromObject({}),
      ConstructionPhase: obj.ConstructionPhase
        ? ConstructionPhase.fromObject(obj.ConstructionPhase)
        : ConstructionPhase.fromObject({}),
      PlanPrep: obj.PlanPrep
        ? PlanPrep.fromObject(obj.PlanPrep)
        : PlanPrep.fromObject({}),
      Tabulation: obj.Tabulation
        ? Tabulation.fromObject(obj.Tabulation)
        : Tabulation.fromObject({}),
      Asset: obj.Asset ? Asset.fromObject(obj.Asset) : Asset.fromObject({}),
      QAQC: obj.QAQC ? QAQC.fromObject(obj.QAQC) : QAQC.fromObject({}),
      Help: obj.Help ? Help.fromObject(obj.Help) : Help.fromObject({}),
      Remarks: obj.Remarks
        ? Remarks.fromObject(obj.Remarks)
        : Remarks.fromObject({}),
    });
  }

  /**
   * Converts the DOTPayItem to a plain object (useful for serialization)
   * @returns Plain object representation
   */
  toObject(): Record<string, unknown> {
    return {
      Index: this.Index,
      SPECBOOK_FILTER: this.SPECBOOK_FILTER,
      REFITEM_SEARCH: this.REFITEM_SEARCH,
      PayItem: this.PayItem.toObject(),
      Quantity: this.Quantity.toObject(),
      Funding: this.Funding.toObject(),
      Cost: this.Cost.toObject(),
      Time: this.Time.toObject(),
      ConstructionPhase: this.ConstructionPhase.toObject(),
      PlanPrep: this.PlanPrep.toObject(),
      Tabulation: this.Tabulation.toObject(),
      Asset: this.Asset.toObject(),
      QAQC: this.QAQC.toObject(),
      Help: this.Help.toObject(),
      Remarks: this.Remarks.toObject(),
    };
  }
}


